class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = this.readVariants();
    if (!this.variants.length) return;

    this.sectionId = this.dataset.section;
    this.groups = Array.from(this.querySelectorAll('[data-option-position]'));
    this.optionValues = this.groups.map((group) =>
      Array.from(group.querySelectorAll('input[type="radio"]'), (input) => input.value)
    );
    this.note = this.querySelector('[data-picker-note]');
    this.requestToken = 0;

    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);
    this.syncSelection(this.readSelection(), null);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  normalize(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  sameOption(a, b) {
    return this.normalize(a) === this.normalize(b);
  }

  readVariants() {
    const script = this.querySelector('[data-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent).map((variant) => {
        const options = Array.isArray(variant.options)
          ? variant.options.map((value) => String(value))
          : [variant.option1, variant.option2, variant.option3]
              .filter((value) => value != null)
              .map(String);
        return Object.assign({}, variant, { options });
      });
    } catch (error) {
      return [];
    }
  }

  readSelection() {
    return this.groups.map((group) => {
      const checked = group.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
  }

  onChange(event) {
    const input = event.target;
    if (!input.matches('input[type="radio"]')) return;

    const group = input.closest('[data-option-position]');
    const changedIndex = this.groups.indexOf(group);
    this.syncSelection(this.readSelection(), changedIndex);
  }

  /**
   * Resolves the closest purchasable variant, keeping the option the customer just
   * clicked. Picking a colour never leads to a dead end: the remaining options fall
   * back to the nearest value that actually exists in that colour.
   */
  resolveVariant(selection, changedIndex) {
    const matches = (variant, values) =>
      values.every((value, i) => value == null || this.sameOption(variant.options[i], value));

    const exact = this.variants.find((variant) => matches(variant, selection));
    if (exact && exact.available) return { variant: exact, adjusted: false };

    const lockedIndex = changedIndex === null ? -1 : changedIndex;
    const candidates = this.variants.filter((variant) => {
      if (!variant.available) return false;
      if (lockedIndex < 0) return true;
      return this.sameOption(variant.options[lockedIndex], selection[lockedIndex]);
    });

    if (candidates.length) {
      const distance = (variant) =>
        selection.reduce((total, value, i) => {
          if (i === lockedIndex || this.sameOption(variant.options[i], value)) return total;
          const values = this.optionValues[i] || [];
          const from = values.findIndex((item) => this.sameOption(item, value));
          const to = values.findIndex((item) => this.sameOption(item, variant.options[i]));
          return total + (from === -1 || to === -1 ? values.length : Math.abs(from - to));
        }, 0);

      const nearest = candidates.reduce((best, variant) =>
        distance(variant) < distance(best) ? variant : best
      );
      const adjusted = !exact || nearest.id !== exact.id;
      return { variant: nearest, adjusted };
    }

    const anyAvailable = this.variants.find((variant) => variant.available);
    return { variant: exact || anyAvailable || null, adjusted: Boolean(exact || anyAvailable) };
  }

  syncSelection(selection, changedIndex) {
    const { variant, adjusted } = this.resolveVariant(selection, changedIndex);
    this.currentVariant = variant;

    const applied = variant ? variant.options : selection;
    this.checkInputs(applied);
    this.updateLegends(applied);
    this.updateAvailability(applied);
    this.showNote(adjusted && changedIndex !== null ? applied : null, changedIndex);

    if (!variant) {
      this.toggleAddButton(true, window.variantStrings?.unavailable);
      return;
    }

    this.toggleAddButton(!variant.available, window.variantStrings?.soldOut);
    this.updateFormInput(variant);
    this.updateUrl(variant);
    this.updateMedia(variant);
    this.renderProductInfo(variant);
  }

  checkInputs(applied) {
    this.groups.forEach((group, index) => {
      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = this.sameOption(input.value, applied[index]);
      });
    });
  }

  updateLegends(applied) {
    this.groups.forEach((group, index) => {
      const output = group.querySelector('[data-selected-value]');
      const checked = group.querySelector('input[type="radio"]:checked');
      const label = applied[index] || (checked && checked.value);
      if (output && label) output.textContent = checked ? checked.value : label;
    });
  }

  updateAvailability(applied) {
    this.groups.forEach((group, index) => {
      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const candidate = applied.slice();
        candidate[index] = input.value;
        const exists = this.variants.some(
          (variant) =>
            variant.available &&
            candidate.every((value, i) => this.sameOption(variant.options[i], value))
        );
        input.classList.toggle('is-unavailable', !exists);
        const label = group.querySelector(`label[for="${input.id}"]`);
        if (label) label.classList.toggle('is-unavailable', !exists);
      });
    });
  }

  showNote(applied, changedIndex) {
    if (!this.note) return;
    if (!applied) {
      this.note.textContent = '';
      this.note.hidden = true;
      return;
    }

    const template = this.note.dataset.pickerNote;
    if (!template) return;

    const other = this.groups
      .map((group, index) => {
        if (index === changedIndex) return null;
        const checked = group.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : applied[index];
      })
      .filter(Boolean)
      .join(' / ');

    this.note.textContent = template.replace('[value]', other);
    this.note.hidden = false;
  }

  toggleAddButton(disabled, text) {
    const buttons = document.querySelectorAll(
      `#product-form-${this.sectionId} [name="add"], [data-buy-button="${this.sectionId}"]`
    );

    buttons.forEach((button) => {
      button.disabled = Boolean(disabled);
      button.toggleAttribute('disabled', Boolean(disabled));
      const label = button.querySelector('span');
      if (!label) return;
      label.textContent = disabled ? text || label.textContent : window.variantStrings?.addToCart || label.textContent;
    });
  }

  updateFormInput(variant) {
    document
      .querySelectorAll(
        `#product-form-${this.sectionId} input[name="id"], #product-form-installment-${this.sectionId} input[name="id"]`
      )
      .forEach((input) => {
        input.disabled = false;
        input.removeAttribute('disabled');
        input.value = String(variant.id);
      });
  }

  updateUrl(variant) {
    if (this.dataset.updateUrl === 'false') return;
    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  }

  updateMedia(variant) {
    const mediaId = variant.featured_media && variant.featured_media.id;
    if (!mediaId) return;
    const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
    if (gallery && typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(`${this.sectionId}-${mediaId}`, true);
    }
  }

  renderProductInfo(variant) {
    const token = ++this.requestToken;
    const source = this.dataset.originalSection || this.sectionId;
    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('variant', variant.id);
    url.searchParams.set('section_id', source);

    fetch(url.pathname + url.search)
      .then((response) => response.text())
      .then((text) => {
        if (token !== this.requestToken) return;
        const html = new DOMParser().parseFromString(text, 'text/html');

        [
          [`price-${source}`, `price-${this.sectionId}`],
          [`Sku-${source}`, `Sku-${this.sectionId}`],
          [`subtotal-${source}`, `subtotal-${this.sectionId}`],
        ].forEach(([fromId, toId]) => {
          const from = html.getElementById(fromId);
          const to = document.getElementById(toId);
          if (!from || !to) return;
          to.innerHTML = from.innerHTML;
          to.classList.toggle('visibility-hidden', from.classList.contains('visibility-hidden'));
        });

        const stickyPrice = document.getElementById(`sticky-price-${this.sectionId}`);
        const priceSource = html.getElementById(`price-${source}`);
        if (stickyPrice && priceSource) stickyPrice.innerHTML = priceSource.innerHTML;
      })
      .catch(() => {});
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
